import axios from "axios";
import { Location } from "history";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Category, NotFound, Product } from "src/pages";

import i18n from "../../../src/i18n";
import LoadingPage from "../../components/Pages/LoadingPage";
import environment from "../../environment";
import { IUrlState } from "../../types/global";
import { CMSTemplate } from "../CMS";

interface IGutterProps {
  location: Location;
}

const fetchUrlState = async (url: string): Promise<IUrlState | null> => {
  try {
    const { data } = await axios.get(
      `${environment.REACT_APP_API_ENDPOINT}/path/${url.replace(/^\/+/, "")}`
    );

    return data;
  } catch (err) {
    return null;
  }
};

const Gutter = ({ location }: IGutterProps): JSX.Element => {
  const dispatch = useDispatch();
  const [urlState, setUrlState] = useState<IUrlState | null>(null);

  const dispatchUrlStateReceived = useCallback(
    (payload: IUrlState): void => {
      setUrlState(payload);

      if (payload && payload.lang) {
        i18n.changeLanguage(payload.lang);
      }
      dispatch({ payload, type: "URL_STATE_RECEIVED" });
    },
    [setUrlState, dispatch]
  );

  useEffect(() => {
    let mounted = true;
    if (window.__initialUrlState) {
      // We have a state prepopulated in window object, we use it as default and then remove it.
      dispatchUrlStateReceived(window.__initialUrlState);
      window.__initialUrlState = undefined;
    } else {
      (async (): Promise<void> => {
        // axios call:
        const urlStatePayload = await fetchUrlState(location.pathname);
        if (mounted && urlStatePayload) {
          dispatchUrlStateReceived(urlStatePayload);
        }
      })();
    }

    return (): void => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchUrlStateReceived, location.pathname]);

  if (urlState && (!urlState.route || urlState.route.error)) {
    return <NotFound />;
  }

  if (urlState && urlState.route) {
    if (urlState.route.is_cms) {
      const replaceSlash = (url: string): string => url.replace(/\//g, "___");

      const cmsId = replaceSlash(
        (urlState.lang !== "fr" ? urlState.lang + "/" : "") + urlState.url
      );

      return <CMSTemplate cmsType="page_cms_import" cmsId={cmsId} />;
    }

    if (urlState.route.product_id) {
      return <Product productId={String(urlState.route.product_id)} />;
    }

    if (urlState.route.category_id) {
      const categoryId = String(urlState.route.category_id);

      return <Category key={categoryId} categoryId={categoryId} />;
    }
  }

  return <LoadingPage />;
};

export default Gutter;
